/** @jsx jsx */
/** @jsxRuntime classic */

import { jsx } from '@emotion/react';
import React, { CSSProperties } from 'react';
import cx from 'classnames';
import useTheme from '../style/useTheme';
import Typography from '../Typography/Typography';
import useEvents from '../events/useEvents';
import { CardEvent, OnClickEvent } from '../events/EventProvider';
import InteractiveDiv from '../InteractiveDiv/InteractiveDiv';

export type Props = React.PropsWithChildren<{
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  title?: string;
  subtitle?: string;
  eventId?: string;
  'data-testid'?: string;
  onClick?: () => void;
  tabIndex?: number;
  sx?: CSSProperties;
}>;

const SimpleCard: React.FC<Props> = ({
  children,
  title,
  subtitle,
  className,
  eventId,
  onClick,
  sx,
  tabIndex,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  ...props
}) => {
  const { theme, resolveColor } = useTheme();

  const triggerEvent = useEvents<OnClickEvent<CardEvent, EventTarget>>(
    eventId,
    'Card'
  );

  const handleClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
    if (onClick) {
      onClick();
    }
    triggerEvent({
      target: event.target,
    });
  };

  // Since <SimpleCard> can be used as either a non-interactive or clickable component, we can differentiate between
  // the two cases by checking whether an `onClick` handler is defined. If it is, then we should use an
  // <InteractiveDiv> component instead of a <div> to ensure that accessibility requirements are satisfied.
  const ContainerElement = onClick ? InteractiveDiv : 'div';

  return (
    <ContainerElement
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      data-testid={props['data-testid'] || 'SimpleCard'}
      className={cx('origami-SimpleCard', className)}
      tabIndex={tabIndex}
      onClick={handleClick}
      css={{
        border: `1px solid ${resolveColor(
          theme.components.card['border-color']
        )}`,
        backgroundColor: resolveColor(
          theme.components.card['background-color']
        ),
        borderRadius: '8px',
        width: '100%',
        height: 'fit-content',
        padding: '12px 16px',
        boxSizing: 'border-box',
        '&::after': {
          boxSizing: 'border-box',
        },
        '&::before': {
          boxSizing: 'border-box',
        },
        ...sx,
      }}
    >
      {title && (
        <Typography sx={{ margin: 0 }} variant="label">
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography sx={{ margin: 0 }} color="light" variant="caption">
          {subtitle}
        </Typography>
      )}
      <div className="origami-SimpleCard-contents">{children}</div>
    </ContainerElement>
  );
};

export default SimpleCard;
